/**
 * ProfileStatsTab — Admin view of user profile completion.
 * Read-only. Shows per-user completion %, missing fields, last updated, job alerts.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { API_BASE, getAdminToken, formatDate } from './adminUtils';
import { Search, RefreshCw, UserCircle, ChevronUp, ChevronDown } from 'lucide-react';

// ── helpers ────────────────────────────────────────────────────────────────

const pctColor = (pct) => {
  if (pct === 100) return 'bg-emerald-500';
  if (pct >= 60)  return 'bg-blue-500';
  if (pct >= 30)  return 'bg-amber-500';
  return 'bg-red-500';
};

const pctTextColor = (pct) => {
  if (pct === 100) return 'text-emerald-400';
  if (pct >= 60)  return 'text-blue-400';
  if (pct >= 30)  return 'text-amber-400';
  return 'text-red-400';
};

const FILTERS = [
  { value: 'all',     label: 'All' },
  { value: 'complete', label: '✅ Complete (100%)' },
  { value: 'good',    label: '🔵 Good (60–99%)' },
  { value: 'low',     label: '🟡 Low (30–59%)' },
  { value: 'bare',    label: '🔴 Bare (<30%)' },
  { value: 'never',   label: '⏳ Never Updated' },
  { value: 'jobs',    label: '🔔 Job Alerts ON' },
];

// ── summary card ───────────────────────────────────────────────────────────

const SummaryCard = ({ label, value, sub, color }) => (
  <div className={`rounded-xl p-4 border ${color}`}>
    <p className="text-2xl font-bold text-white">{value}</p>
    <p className="text-xs font-medium text-slate-300 mt-0.5">{label}</p>
    {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
  </div>
);

// ── main component ─────────────────────────────────────────────────────────

export const ProfileStatsTab = () => {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('all');
  const [search, setSearch]     = useState('');
  const [sortBy, setSortBy]     = useState('completion_pct');
  const [sortDir, setSortDir]   = useState('asc');  // asc = worst first (most pending)

  const adminToken = getAdminToken();

  const fetchStats = useCallback(async () => {
    if (!adminToken) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/user-profile-stats`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (!res.ok) throw new Error('Failed');
      setData(await res.json());
    } catch {
      toast.error('Failed to load profile stats');
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // ── filter + search + sort ──────────────────────────────────────────────

  const filtered = (data?.users || []).filter((u) => {
    // text search
    if (search) {
      const q = search.toLowerCase();
      const name = `${u.first_name} ${u.last_name}`.toLowerCase();
      if (!name.includes(q) && !u.email.toLowerCase().includes(q)) return false;
    }
    // category filter
    switch (filter) {
      case 'complete': return u.completion_pct === 100;
      case 'good':     return u.completion_pct >= 60 && u.completion_pct < 100;
      case 'low':      return u.completion_pct >= 30 && u.completion_pct < 60;
      case 'bare':     return u.completion_pct < 30;
      case 'never':    return !u.last_updated;
      case 'jobs':     return u.job_notifications;
      default:         return true;
    }
  }).sort((a, b) => {
    let va = a[sortBy], vb = b[sortBy];
    if (sortBy === 'name') { va = `${a.first_name} ${a.last_name}`; vb = `${b.first_name} ${b.last_name}`; }
    if (sortBy === 'last_updated') { va = va || ''; vb = vb || ''; }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('asc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ChevronUp className="w-3 h-3 text-slate-600 inline ml-0.5" />;
    return sortDir === 'asc'
      ? <ChevronUp className="w-3 h-3 text-orange-400 inline ml-0.5" />
      : <ChevronDown className="w-3 h-3 text-orange-400 inline ml-0.5" />;
  };

  // ── render ──────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500" />
      </div>
    );
  }

  const s = data?.summary;

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <UserCircle className="w-5 h-5 text-orange-400" />
            Profile Completion Stats
          </h2>
          <p className="text-sm text-slate-400 mt-0.5">
            {s?.total} users · avg {s?.avg_completion}% complete
          </p>
        </div>
        <button
          onClick={fetchStats}
          className="flex items-center gap-2 bg-slate-700/60 hover:bg-slate-700 text-slate-300 text-sm px-3 py-2 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Summary cards */}
      {s && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <SummaryCard label="Total Users"     value={s.total}         color="bg-slate-800/60 border-slate-700/50" />
          <SummaryCard label="Avg Completion"  value={`${s.avg_completion}%`} color="bg-slate-800/60 border-slate-700/50" />
          <SummaryCard label="Complete (100%)" value={s.complete}      color="bg-emerald-500/10 border-emerald-500/30" />
          <SummaryCard label="Good (≥60%)"     value={s.good}          color="bg-blue-500/10 border-blue-500/30" />
          <SummaryCard label="Incomplete (<60%)" value={s.low}          color="bg-amber-500/10 border-amber-500/30" />
          <SummaryCard label="Job Alerts ON"   value={s.job_alerts_on} color="bg-orange-500/10 border-orange-500/30"
            sub={`${s.never_updated} never updated profile`}
          />
        </div>
      )}

      {/* Avg completion bar */}
      {s && (
        <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl px-5 py-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-slate-400">Average Profile Completion</p>
            <p className={`text-sm font-bold ${pctTextColor(s.avg_completion)}`}>{s.avg_completion}%</p>
          </div>
          <div className="h-2 w-full rounded-full bg-slate-700">
            <div
              className={`h-2 rounded-full transition-all duration-500 ${pctColor(s.avg_completion)}`}
              style={{ width: `${s.avg_completion}%` }}
            />
          </div>
          <div className="flex justify-between text-[11px] text-slate-600 mt-1.5">
            <span>0%</span><span>50%</span><span>100%</span>
          </div>
        </div>
      )}

      {/* Filters + Search */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filter === f.value
                ? 'bg-orange-500 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            {f.label}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name / email…"
            className="bg-slate-800/60 border border-slate-600 rounded-lg pl-8 pr-3 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-orange-500/50 w-48"
          />
        </div>
      </div>

      {/* Count */}
      <p className="text-xs text-slate-500">
        Showing {filtered.length} of {data?.users?.length || 0} users
      </p>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-slate-700/50">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-700/50 bg-slate-800/60">
              <th
                onClick={() => toggleSort('name')}
                className="px-4 py-3 text-left text-xs font-medium text-slate-400 cursor-pointer hover:text-white select-none"
              >
                User <SortIcon col="name" />
              </th>
              <th
                onClick={() => toggleSort('completion_pct')}
                className="px-4 py-3 text-left text-xs font-medium text-slate-400 cursor-pointer hover:text-white select-none w-48"
              >
                Completion <SortIcon col="completion_pct" />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400">Missing Fields</th>
              <th
                onClick={() => toggleSort('last_updated')}
                className="px-4 py-3 text-left text-xs font-medium text-slate-400 cursor-pointer hover:text-white select-none"
              >
                Last Updated <SortIcon col="last_updated" />
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-slate-400">Job Alerts</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/30">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-12 text-slate-500 text-sm">
                  No users match this filter.
                </td>
              </tr>
            ) : (
              filtered.map((u) => (
                <tr key={u.id} className="hover:bg-slate-700/20 transition-colors">

                  {/* User */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-white">
                          {(u.first_name?.[0] || '?').toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                          {u.first_name} {u.last_name}
                        </p>
                        <p className="text-xs text-slate-500 truncate">{u.email}</p>
                      </div>
                    </div>
                  </td>

                  {/* Completion */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden min-w-[80px]">
                        <div
                          className={`h-2 rounded-full ${pctColor(u.completion_pct)}`}
                          style={{ width: `${u.completion_pct}%` }}
                        />
                      </div>
                      <span className={`text-xs font-bold w-8 text-right ${pctTextColor(u.completion_pct)}`}>
                        {u.completion_pct}%
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-600 mt-0.5">
                      {u.filled_fields.length} / 9 fields
                    </p>
                  </td>

                  {/* Missing fields */}
                  <td className="px-4 py-3">
                    {u.missing_fields.length === 0 ? (
                      <span className="text-xs text-emerald-400 font-medium">✓ All filled</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {u.missing_fields.map((f) => (
                          <span
                            key={f}
                            className="text-[10px] bg-slate-700/60 text-slate-400 px-1.5 py-0.5 rounded"
                          >
                            {f}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>

                  {/* Last Updated */}
                  <td className="px-4 py-3">
                    {u.last_updated ? (
                      <span className="text-xs text-slate-400">
                        {formatDate(u.last_updated)}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-600 italic">Never updated</span>
                    )}
                  </td>

                  {/* Job Alerts */}
                  <td className="px-4 py-3 text-center">
                    {u.job_notifications ? (
                      <span className="inline-flex items-center gap-1 text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">
                        🔔 {u.job_preference || 'any'}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-600">—</span>
                    )}
                  </td>

                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
};
